import { savedViewsApi } from '@/lib/api/saved-views.api'
import { userPreferencesApi } from '@/lib/api/user-preferences.api'
import { createLogger } from '@/lib/logger'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  ModuleTypeEnum,
  PreferenceContextEnum,
  PreferenceScopeEnum,
  SavedQueryVisibilityEnum,
  SavedView,
  SavedViewRecord,
  UserPreferenceData,
  ViewConfig,
  ViewPreferences,
} from '@scrambl/shared'

const logger = createLogger('hooks/useModuleViews')

interface UseModuleViewsOptions {
  moduleType: ModuleTypeEnum
  canManage?: boolean
}

interface UseModuleViewsResult {
  views: SavedView[]
  userViews: SavedView[]
  orgViews: SavedView[]
  activeViewId?: string
  activeView?: SavedView
  orgDefaultViewId?: string
  isLoading: boolean
  setActiveViewId: (viewId: string) => Promise<void>
  /**
   * `query` is the ScQL from the surface's query bar, when it has one. Passing
   * it stores the query and the table config on the SAME record.
   */
  saveActiveView: (config: ViewConfig, query?: string | null) => Promise<void>
  createView: (
    name: string,
    config: ViewConfig,
    scope?: PreferenceScopeEnum,
    query?: string | null,
  ) => Promise<void>
  setOrgDefaultViewId: (viewId: string) => Promise<void>
  deleteView: (viewId: string) => Promise<void>
}

const EMPTY_PREFS: ViewPreferences = { views: [] }
const EMPTY_RECORDS: SavedViewRecord[] = []

const createViewId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `view_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

const recordScope = (record: SavedViewRecord): PreferenceScopeEnum =>
  record.visibility === SavedQueryVisibilityEnum.ORGANIZATION
    ? PreferenceScopeEnum.ORGANIZATION
    : PreferenceScopeEnum.USER

const toSavedView = (record: SavedViewRecord): SavedView => ({
  id: record.id,
  name: record.name || 'Untitled view',
  scope: recordScope(record),
  config: record.config || {},
  createdAt:
    typeof record.createdAt === 'string'
      ? record.createdAt
      : record.createdAt?.toISOString(),
  updatedAt:
    typeof record.updatedAt === 'string'
      ? record.updatedAt
      : record.updatedAt?.toISOString(),
  query: record.query,
  source: 'record',
})

const normalizeLegacyViews = (
  views: SavedView[] | undefined,
  scope: PreferenceScopeEnum,
): SavedView[] =>
  (views || []).map(view => ({
    id: view.id || createViewId(),
    name: view.name || 'Untitled view',
    scope: view.scope || scope,
    config: view.config || {},
    createdAt: view.createdAt,
    updatedAt: view.updatedAt,
    source: 'legacy' as const,
  }))

const toViewPreferences = (
  preferences?: Record<string, unknown>,
): ViewPreferences => {
  if (!preferences) return EMPTY_PREFS
  const rawViews = (preferences as { views?: unknown }).views
  const activeViewId = (preferences as { activeViewId?: unknown }).activeViewId
  const defaultViewId = (preferences as { defaultViewId?: unknown })
    .defaultViewId

  return {
    views: Array.isArray(rawViews) ? (rawViews as SavedView[]) : [],
    activeViewId: typeof activeViewId === 'string' ? activeViewId : undefined,
    defaultViewId:
      typeof defaultViewId === 'string' ? defaultViewId : undefined,
  }
}

const toPreferencesRecord = (
  preferences: ViewPreferences,
): Record<string, unknown> => preferences as unknown as Record<string, unknown>

/**
 * Saved views for a module list surface.
 *
 * Views are first-class `saved_queries` rows (`/saved-views`) — the same
 * record type as a saved ScQL query, so one artifact can hold a query, a
 * column layout and a grouping together.
 *
 * Two things deliberately stay in the `UserPreference` views blob because they
 * are per-viewer selection state rather than part of the shared artifact:
 * `activeViewId` (which view *I* am looking at) and `defaultViewId` (which
 * shared view the org opens by default).
 *
 * The blob's `views` array is never deleted. Anything still in it that the
 * backfill migration did not promote is shown alongside the records and gets
 * promoted the first time it is saved, so no view can go missing.
 */
export function useModuleViews({
  moduleType,
  canManage = false,
}: UseModuleViewsOptions): UseModuleViewsResult {
  const queryClient = useQueryClient()

  const { data: viewRecords = EMPTY_RECORDS, isLoading: isLoadingRecords } =
    useQuery({
      queryKey: ['saved-views', moduleType],
      queryFn: async () => {
        try {
          return await savedViewsApi.list(moduleType)
        } catch (error) {
          // A failing view service must never take the table down with it —
          // fall back to whatever the legacy blob still holds.
          logger.error('[useModuleViews] Failed to load saved views', error)
          return EMPTY_RECORDS
        }
      },
      staleTime: 60_000,
    })

  const { data: userPreferenceData, isLoading: isLoadingUser } = useQuery({
    queryKey: ['view-preferences', moduleType, 'user'],
    queryFn: () =>
      userPreferencesApi.getUserPreferences(
        PreferenceContextEnum.VIEWS,
        moduleType,
        PreferenceScopeEnum.USER,
      ) as Promise<UserPreferenceData | null>,
  })

  const { data: orgPreferenceData, isLoading: isLoadingOrg } = useQuery({
    queryKey: ['view-preferences', moduleType, 'org'],
    queryFn: () =>
      userPreferencesApi.getUserPreferences(
        PreferenceContextEnum.VIEWS,
        moduleType,
        PreferenceScopeEnum.ORGANIZATION,
      ) as Promise<UserPreferenceData | null>,
  })

  const userPreferences = toViewPreferences(userPreferenceData?.preferences)
  const orgPreferences = toViewPreferences(orgPreferenceData?.preferences)

  // Raw legacy payloads, kept verbatim so writing `activeViewId` back never
  // rewrites (or loses) the views array we are migrating away from.
  const legacyUserViewsRaw = userPreferences.views
  const legacyOrgViewsRaw = orgPreferences.views

  // A record that carries `legacyViewId` IS the blob entry with that id.
  const migratedLegacyIds = new Set(
    viewRecords
      .map(record => record.legacyViewId)
      .filter((id): id is string => !!id),
  )

  const legacyUserViews = normalizeLegacyViews(
    legacyUserViewsRaw,
    PreferenceScopeEnum.USER,
  ).filter(view => !migratedLegacyIds.has(view.id))
  const legacyOrgViews = normalizeLegacyViews(
    legacyOrgViewsRaw,
    PreferenceScopeEnum.ORGANIZATION,
  ).filter(view => !migratedLegacyIds.has(view.id))

  const recordViews = viewRecords.map(toSavedView)
  const userViews = [
    ...recordViews.filter(view => view.scope === PreferenceScopeEnum.USER),
    ...legacyUserViews,
  ]
  const orgViews = [
    ...recordViews.filter(
      view => view.scope === PreferenceScopeEnum.ORGANIZATION,
    ),
    ...legacyOrgViews,
  ]

  const views = [...orgViews, ...userViews]

  const orgDefaultViewId = orgPreferences.defaultViewId
  const resolvedActiveViewId =
    userPreferences.activeViewId || orgDefaultViewId || views[0]?.id
  const activeView =
    views.find(view => view.id === resolvedActiveViewId) || views[0]

  const refreshQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['saved-views', moduleType] }),
      queryClient.invalidateQueries({
        queryKey: ['view-preferences', moduleType],
      }),
    ])
  }

  const saveUserPreferences = async (nextPreferences: ViewPreferences) => {
    await userPreferencesApi.saveUserPreferences(
      PreferenceContextEnum.VIEWS,
      toPreferencesRecord(nextPreferences),
      moduleType,
      PreferenceScopeEnum.USER,
    )
    await refreshQueries()
  }

  const saveOrgPreferences = async (nextPreferences: ViewPreferences) => {
    await userPreferencesApi.saveUserPreferences(
      PreferenceContextEnum.VIEWS,
      toPreferencesRecord(nextPreferences),
      moduleType,
      PreferenceScopeEnum.ORGANIZATION,
    )
    await refreshQueries()
  }

  /** Only the pointer changes; the legacy views array rides along untouched. */
  const writeActiveViewId = async (viewId: string | undefined) => {
    await saveUserPreferences({
      ...userPreferences,
      views: legacyUserViewsRaw,
      activeViewId: viewId,
    })
  }

  const setActiveViewId = async (viewId: string) => {
    try {
      await writeActiveViewId(viewId)
    } catch (error) {
      logger.error('[useModuleViews] Failed to set active view', error)
      toast.error('Failed to update view selection')
    }
  }

  const createRecordView = async (
    name: string,
    config: ViewConfig,
    scope: PreferenceScopeEnum,
    options: { legacyViewId?: string; query?: string | null } = {},
  ): Promise<SavedViewRecord> =>
    savedViewsApi.create({
      name,
      moduleType,
      config,
      visibility:
        scope === PreferenceScopeEnum.ORGANIZATION
          ? SavedQueryVisibilityEnum.ORGANIZATION
          : SavedQueryVisibilityEnum.PRIVATE,
      ...(options.legacyViewId ? { legacyViewId: options.legacyViewId } : {}),
      ...(options.query !== undefined ? { query: options.query } : {}),
    })

  const createView = async (
    name: string,
    config: ViewConfig,
    scope: PreferenceScopeEnum = PreferenceScopeEnum.USER,
    query?: string | null,
  ) => {
    const targetScope =
      scope === PreferenceScopeEnum.ORGANIZATION && canManage
        ? PreferenceScopeEnum.ORGANIZATION
        : PreferenceScopeEnum.USER

    try {
      const created = await createRecordView(
        name.trim() || 'New view',
        config,
        targetScope,
        { query },
      )
      if (targetScope === PreferenceScopeEnum.ORGANIZATION) {
        await refreshQueries()
      } else {
        await writeActiveViewId(created.id)
      }
    } catch (error) {
      logger.error('[useModuleViews] Failed to create view', error)
      // Surface the server's reason (e.g. shared views need admin rights, or
      // a query needs the ScQL plan) instead of a generic failure.
      toast.error(
        error instanceof Error ? error.message : 'Failed to create view',
      )
    }
  }

  const saveActiveView = async (config: ViewConfig, query?: string | null) => {
    if (!activeView) {
      await createView('My view', config, PreferenceScopeEnum.USER, query)
      return
    }

    const isShared = activeView.scope === PreferenceScopeEnum.ORGANIZATION
    if (isShared && !canManage) {
      // Same rule as before: a member editing a shared view forks it.
      await createView(
        `My ${activeView.name}`,
        config,
        PreferenceScopeEnum.USER,
        query,
      )
      return
    }

    try {
      if (activeView.source === 'legacy') {
        // Promote on write: anything the backfill missed becomes a record the
        // first time someone saves it, keyed back to the blob entry.
        const created = await createRecordView(
          activeView.name,
          config,
          activeView.scope,
          { legacyViewId: activeView.id, query },
        )
        if (!isShared) {
          await writeActiveViewId(created.id)
        } else {
          await refreshQueries()
        }
        return
      }

      await savedViewsApi.update(activeView.id, {
        config,
        ...(query !== undefined ? { query } : {}),
      })
      await refreshQueries()
    } catch (error) {
      logger.error('[useModuleViews] Failed to save view', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to save view',
      )
    }
  }

  const setOrgDefaultViewId = async (viewId: string) => {
    if (!canManage) return
    if (!orgViews.some(view => view.id === viewId)) return
    try {
      await saveOrgPreferences({
        ...orgPreferences,
        views: legacyOrgViewsRaw,
        defaultViewId: viewId,
      })
    } catch (error) {
      logger.error('[useModuleViews] Failed to set org default view', error)
      toast.error('Failed to update default view')
    }
  }

  const deleteView = async (viewId: string) => {
    const view = views.find(item => item.id === viewId)
    if (!view) return

    const isShared = view.scope === PreferenceScopeEnum.ORGANIZATION
    if (isShared && !canManage) {
      toast.error('You do not have permission to delete shared views')
      return
    }

    const storedActiveViewId = userPreferences.activeViewId
    const remaining = views.filter(item => item.id !== viewId)
    const nextActiveViewId =
      storedActiveViewId === viewId
        ? remaining.find(item => item.id === orgDefaultViewId)?.id ||
          remaining[0]?.id
        : storedActiveViewId

    try {
      if (view.source === 'legacy') {
        if (isShared) {
          await saveOrgPreferences({
            ...orgPreferences,
            views: legacyOrgViewsRaw.filter(item => item.id !== viewId),
            defaultViewId:
              orgPreferences.defaultViewId === viewId
                ? undefined
                : orgPreferences.defaultViewId,
          })
        } else {
          await saveUserPreferences({
            ...userPreferences,
            views: legacyUserViewsRaw.filter(item => item.id !== viewId),
            activeViewId: nextActiveViewId,
          })
          return
        }
      } else {
        await savedViewsApi.remove(viewId)
        if (isShared && orgPreferences.defaultViewId === viewId) {
          await saveOrgPreferences({
            ...orgPreferences,
            views: legacyOrgViewsRaw,
            defaultViewId: undefined,
          })
        }
      }

      if (storedActiveViewId === viewId) {
        await writeActiveViewId(nextActiveViewId)
      } else {
        await refreshQueries()
      }
    } catch (error) {
      logger.error('[useModuleViews] Failed to delete view', error)
      toast.error(
        isShared ? 'Failed to delete shared view' : 'Failed to delete view',
      )
    }
  }

  return {
    views,
    userViews,
    orgViews,
    activeViewId: resolvedActiveViewId,
    activeView,
    orgDefaultViewId,
    isLoading: isLoadingRecords || isLoadingUser || isLoadingOrg,
    setActiveViewId,
    saveActiveView,
    createView,
    setOrgDefaultViewId,
    deleteView,
  }
}
